import React, { useState, useCallback } from 'react';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useHistory, useLocation } from 'react-router-dom';
import {
  AffordabilityIcon,
  Amount,
  Badge,
  Box,
  Button,
  EyeIcon,
  Heading,
  PercentIcon,
  QuickFilter,
  QuickFilterGroup,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import api from 'api';
import { LoanDataArraySchemaContract } from 'schemas/LoanDataArraySchema';
import {
  FETCH_MY_LOANS,
  FILTER_STATUS_MAP,
  LOAN_SETTINGS_KEY,
  LOAN_STATUS_COLOR,
  LOAN_STATUS_LABEL,
  STATUS_TO_FILTER_MAP,
} from './constants';
import { formatDate, formatMonthYear, getEmiEndMonth } from './utils';
import { FilterValue } from './types';
import loansRupeesCoin from 'assets/loan/loans-rupees-coin.png';
import { CreateOrEditLoanRequest } from './CreateOrEditLoanRequest';
import { LoanDetailDrawer } from './LoanDetailDrawer';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { useLoanDrawer } from './useLoanDrawer';

type Loan = LoanDataArraySchemaContract['data'][number] & { created_at?: string | null };

export const EmployeeLoansView = () => {
  const history = useHistory();
  const location = useLocation();
  const { data: loanSettings } = useQuery({
    queryKey: [LOAN_SETTINGS_KEY],
    queryFn: api.loans.getLoanSettings,
    staleTime: 5 * 60 * 1000,
  });

  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { loanId: selectedLoanId, openDrawer, closeDrawer } = useLoanDrawer();

  const getInitialFilter = (): FilterValue => {
    const params = new URLSearchParams(location.search);
    const statusParam = Number(params.get('status'));
    return (STATUS_TO_FILTER_MAP[statusParam] as FilterValue) ?? 'all';
  };

  const [filter, setFilter] = useState<FilterValue>(getInitialFilter);

  const handleFilterChange = useCallback(
    (newFilter: FilterValue) => {
      setFilter(newFilter);
      const params = new URLSearchParams();
      const status = FILTER_STATUS_MAP[newFilter];
      if (status !== null) {
        params.set('status', String(status));
      }
      history.replace({ search: params.toString() });
    },
    [history],
  );

  const { debouncedValue: debouncedSearch } = useDebouncedValue(search, 300);
  const trimmed = debouncedSearch.trim();
  const isNumericSearch = !!trimmed && !isNaN(Number(trimmed));
  const loanIdSearch = isNumericSearch ? Number(trimmed) : undefined;
  const categoryNameSearch = !isNumericSearch && trimmed ? trimmed : undefined;

  const { data, isFetching } = useQuery({
    queryKey: [FETCH_MY_LOANS, filter, loanIdSearch, categoryNameSearch],
    queryFn: ({ queryKey }) => {
      const currentFilter = queryKey[1] as FilterValue;
      const currentLoanId = queryKey[2] as number | undefined;
      const currentCategoryName = queryKey[3] as string | undefined;
      const status = FILTER_STATUS_MAP[currentFilter];
      const isArray = Array.isArray(status);
      return api.loans.fetchEmployeeLoanData({
        ...(isArray ? { statuses: status as number[] } : {}),
        ...(!isArray && status !== null ? { status: status as number } : {}),
        ...(currentLoanId ? { loan_id: currentLoanId } : {}),
        ...(currentCategoryName ? { loan_category_name: currentCategoryName } : {}),
      });
    },
  });

  const loans = data?.data ?? [];

  const isFirstTimeEmpty = !isFetching && filter === 'all' && !trimmed && loans.length === 0;
  const isFilterEmpty = !isFetching && !isFirstTimeEmpty && loans.length === 0;

  if (loanSettings && !loanSettings.is_loan_requests_enabled) {
    history.replace('/');
    return null;
  }

  return (
    <Box padding="spacing.8">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="spacing.6">
        <Heading size="large" weight="semibold">
          Loans & Advances
        </Heading>
        {!isFirstTimeEmpty && (
          <Button onClick={() => setIsCreateOpen(true)}>+ Create Request</Button>
        )}
      </Box>

      {isFirstTimeEmpty ? (
        <Box display="flex" gap="spacing.8" flexDirection="column">
          <Box
            height="479px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding="spacing.7"
            flexDirection="column"
            borderRadius="medium"
            borderWidth="thin"
            borderColor="surface.border.gray.muted"
            backgroundColor="surface.background.gray.intense">
            <Box
              width="398px"
              gap="spacing.8"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center">
              <img src={loansRupeesCoin} alt="Loans & Advances" width="120" height="120" />
              <Box textAlign="center">
                <Heading size="small" weight="semibold" color="surface.text.gray.subtle">
                  Request a Loan or Advance
                </Heading>
                <Box width="328px">
                  <Text
                    marginTop="spacing.3"
                    size="medium"
                    variant="body"
                    weight="regular"
                    color="surface.text.gray.muted">
                    Need funds before payday? Request a loan or advance easily from your employer.
                  </Text>
                </Box>
              </Box>
              <Button onClick={() => setIsCreateOpen(true)}>Create Request</Button>
            </Box>
          </Box>
          <Box width="100%" display="flex" gap="spacing.7">
            <Box
              padding="spacing.7"
              display="flex"
              flexDirection="column"
              gap="spacing.5"
              width="100%"
              borderRadius="medium"
              borderWidth="thin"
              borderColor="surface.border.gray.muted"
              backgroundColor="surface.background.gray.intense">
              <PercentIcon size="xlarge" color="surface.icon.primary.normal" />
              <Box display="flex" flexDirection="column" gap="spacing.3">
                <Heading size="small" weight="semibold" color="surface.text.gray.subtle">
                  Low or Zero Interest
                </Heading>
                <Text size="medium" variant="body" weight="regular" color="surface.text.gray.muted">
                  Lower rates than banks or interest-free
                </Text>
              </Box>
            </Box>
            <Box
              padding="spacing.7"
              display="flex"
              flexDirection="column"
              gap="spacing.5"
              width="100%"
              borderRadius="medium"
              borderWidth="thin"
              borderColor="surface.border.gray.muted"
              backgroundColor="surface.background.gray.intense">
              <EyeIcon size="xlarge" color="surface.icon.primary.normal" />
              <Box display="flex" flexDirection="column" gap="spacing.3">
                <Heading size="small" weight="semibold" color="surface.text.gray.subtle">
                  Track Your Requests
                </Heading>
                <Text size="medium" variant="body" weight="regular" color="surface.text.gray.muted">
                  Track Approvals, EMIs, and Closure Dates
                </Text>
              </Box>
            </Box>
            <Box
              padding="spacing.7"
              display="flex"
              flexDirection="column"
              gap="spacing.5"
              width="100%"
              borderRadius="medium"
              borderWidth="thin"
              borderColor="surface.border.gray.muted"
              backgroundColor="surface.background.gray.intense">
              <AffordabilityIcon size="xlarge" color="surface.icon.primary.normal" />
              <Box display="flex" flexDirection="column" gap="spacing.3">
                <Heading size="small" weight="semibold" color="surface.text.gray.subtle">
                  Easy Repayment
                </Heading>
                <Text size="medium" variant="body" weight="regular" color="surface.text.gray.muted">
                  Repay directly from your salary to avoid missed payments
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginBottom="spacing.5">
            <QuickFilterGroup
              selectionType="single"
              defaultValue="all"
              value={filter}
              onChange={(e) => handleFilterChange(e.values[0] as FilterValue)}>
              <Box display="flex" gap="spacing.3">
                <QuickFilter value="all" title="All" />
                <QuickFilter value="pending" title="Pending" />
                <QuickFilter value="active" title="Active" />
                <QuickFilter value="closed" title="Closed" />
              </Box>
            </QuickFilterGroup>

            <SearchInput
              label=""
              placeholder="Search ID or Reason"
              value={search}
              onChange={({ value }) => setSearch(value ?? '')}
              onClearButtonClick={() => setSearch('')}
            />
          </Box>

          {isFilterEmpty ? (
            <EmptyListView
              title={trimmed ? 'No results found' : `No ${filter} loans`}
              description={
                <Text color="surface.text.gray.muted" textAlign="center">
                  {trimmed
                    ? `No loans match your search.`
                    : `No ${filter} loans found for your account.`}
                </Text>
              }
            />
          ) : (
            <Table<Loan>
              data={{ nodes: loans }}
              isLoading={isFetching}
              gridTemplateColumns="1fr 1.5fr 0.8fr 0.8fr 1.3fr 0.8fr 0.8fr">
              {(tableData) => (
                <>
                  <TableHeader>
                    <TableHeaderRow>
                      <TableHeaderCell>Requested On</TableHeaderCell>
                      <TableHeaderCell>Reason</TableHeaderCell>
                      <TableHeaderCell>EMI Start Month</TableHeaderCell>
                      <TableHeaderCell>EMI End Month</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell textAlign="right">EMI Amount</TableHeaderCell>
                      <TableHeaderCell textAlign="right">Amount</TableHeaderCell>
                    </TableHeaderRow>
                  </TableHeader>
                  <TableBody>
                    {tableData.map((loan) => (
                      <TableRow key={loan.id} item={loan} onClick={() => openDrawer(loan.id)}>
                        <TableCell>
                          <Text size="small">{formatDate(loan.created_at)}</Text>
                        </TableCell>
                        <TableCell>
                          <Text size="small" truncateAfterLines={1}>
                            {loan.loan_category_name ?? '—'}
                          </Text>
                        </TableCell>
                        <TableCell>
                          <Text size="small">{formatMonthYear(loan.recovery_start_from)}</Text>
                        </TableCell>
                        <TableCell>
                          <Text size="small">
                            {getEmiEndMonth(loan.recovery_start_from, loan.tenure)}
                          </Text>
                        </TableCell>
                        <TableCell>
                          <Badge color={LOAN_STATUS_COLOR[loan.status] ?? 'neutral'}>
                            {LOAN_STATUS_LABEL[loan.status] ?? 'Unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell textAlign="right">
                          <Amount value={loan.monthly_total_emi ?? 0} size="small" />
                        </TableCell>
                        <TableCell textAlign="right">
                          <Amount value={loan.principal} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              )}
            </Table>
          )}
        </>
      )}

      <CreateOrEditLoanRequest isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
      <LoanDetailDrawer isOpen={!!selectedLoanId} onDismiss={closeDrawer} loanId={selectedLoanId} />
    </Box>
  );
};

export default EmployeeLoansView;
